/**
 * Felder des Monats-Editors (Board „Monat je Apartment“): Verfügbarkeit, Preis, Mindestnächte und
 * „Erweitert“ mit Höchstnächten (0 = unbegrenzt) und den drei Beschränkungen. Leere Felder bleiben
 * unverändert; „Verfügbare Einheiten“ gibt es nicht – das Lesemodell kennt nur frei oder nicht.
 */
import { useId, type ReactNode } from 'react';
import { RESTRICTIONS } from '@/features/properties/ari-edit';
import { RESTRICTION_MODES } from '@/features/properties/ari-edit/multiRangeRestrictions';
import {
  hasAdvancedValue,
  type InlineEditorValues,
  type RestrictionKey,
} from '../calendar/inlineEditorModel';
import { SegmentedChoice } from './SegmentedChoice';

const AVAILABILITY = [
  { value: '', label: 'Unverändert' },
  { value: 'available', label: 'Freigeben' },
  { value: 'blocked', label: 'Blockieren' },
] as const;

interface Props {
  values: InlineEditorValues;
  onChange: (patch: Partial<InlineEditorValues>) => void;
  disabled: boolean;
  pricePlaceholder: string;
  minStayPlaceholder: string;
}

export function MonthEditorFields({ values, onChange, disabled, ...placeholders }: Props) {
  const ids = useId();
  return (
    <div className="flex flex-col gap-3.5">
      <SegmentedChoice
        label="Verfügbarkeit"
        options={AVAILABILITY}
        value={values.availability}
        onChange={(availability) => onChange({ availability })}
        disabled={disabled}
        stretch
      />
      <div className="grid grid-cols-2 gap-2.5">
        <BoxField id={`${ids}-rate`} label="Preis pro Nacht · €">
          <input
            id={`${ids}-rate`}
            type="number"
            min={0}
            step="0.01"
            inputMode="decimal"
            value={values.rate}
            placeholder={placeholders.pricePlaceholder}
            disabled={disabled}
            onChange={(event) => onChange({ rate: event.target.value })}
            className="placeholder:text-ink-faint w-full min-w-0 bg-transparent text-base font-bold outline-none placeholder:text-sm placeholder:font-normal"
          />
        </BoxField>
        <BoxField id={`${ids}-min`} label="Mindestnächte">
          <input
            id={`${ids}-min`}
            type="number"
            min={1}
            max={365}
            step={1}
            inputMode="numeric"
            value={values.minStay}
            placeholder={placeholders.minStayPlaceholder}
            disabled={disabled}
            onChange={(event) => onChange({ minStay: event.target.value })}
            className="placeholder:text-ink-faint w-full min-w-0 bg-transparent text-base font-bold outline-none placeholder:text-sm placeholder:font-normal"
          />
        </BoxField>
      </div>
      {/* Offen, sobald dort etwas gesetzt ist – sonst verschwände es beim Wiederöffnen des Blatts. */}
      <details
        open={hasAdvancedValue(values) || undefined}
        className="border-hairline rounded-control border px-3.5"
      >
        <summary className="flex min-h-12 cursor-pointer items-center text-[13px] font-bold">
          Erweitert: Höchstnächte, An- und Abreise, Verkaufsstopp
        </summary>
        <div className="flex flex-col gap-3 pb-3.5">
          <BoxField id={`${ids}-max`} label="Höchstnächte · 0 = unbegrenzt">
            <input
              id={`${ids}-max`}
              type="number"
              min={0}
              max={365}
              step={1}
              inputMode="numeric"
              value={values.maxStay}
              placeholder="unverändert"
              disabled={disabled}
              onChange={(event) => onChange({ maxStay: event.target.value })}
              className="placeholder:text-ink-faint w-full bg-transparent text-sm font-semibold outline-none"
            />
          </BoxField>
          {RESTRICTIONS.map((restriction) => (
            <div key={restriction.key} className="flex flex-wrap items-center gap-2.5">
              <span className="grow text-[13px] font-semibold" title={restriction.hint}>
                {restriction.label}
              </span>
              <SegmentedChoice
                label={restriction.label}
                options={RESTRICTION_MODES}
                value={values[restriction.key as RestrictionKey]}
                onChange={(mode) => onChange({ [restriction.key]: mode })}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function BoxField({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <label
      htmlFor={id}
      className="border-line rounded-control focus-within:border-forest flex min-h-[58px] flex-col justify-center gap-0.5 border px-3.5 py-2"
    >
      <span className="text-ink-soft font-mono text-[10px] font-semibold uppercase tracking-[0.1em]">
        {label}
      </span>
      {children}
    </label>
  );
}
